import React, { useEffect, useState } from 'react';
import { RefreshCw, RadioTower } from 'lucide-react';
import axios from 'axios';

// Location Push status: phones the server can wake for a fix even when the
// app is closed (iOS extension, app 2026.10.28+). Backend:
// Backend/app/api/admin_location_push.py (GET /admin/location-push/stats).

interface VersionRow { version: string; users: number; with_token: number }
interface SourceRow { source: string | null; label: string; count: number }
interface RecentRow { username: string; app_version?: string; last_fix?: string | null; fixes_24h: number }
interface Stats {
    devices_with_token: number;
    users_with_token: number;
    locationpush_last_hour: number;
    locationpush_last_24h: number;
    active_ios_users: number;
    active_ios_with_token: number;
    by_version: VersionRow[];
    sources_24h: SourceRow[];
    recent_devices: RecentRow[];
}

const SOURCE_COLORS: Record<string, string> = {
    locationpush: 'bg-green-500', live: 'bg-blue-500', push: 'bg-orange-400',
};

const fmt = (n: number) => n.toLocaleString('tr-TR');

const timeAgo = (iso?: string | null) => {
    if (!iso) return '—';
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return 'şimdi';
    if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
    if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
    return `${Math.floor(s / 86400)} gün önce`;
};

const LocationPushStatus: React.FC = () => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const fetchStats = async () => {
        setRefreshing(true);
        try {
            const response = await axios.get('https://api.lifeshare.app/api/v1/admin/location-push/stats');
            setStats(response.data);
            setError('');
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Veriler alınamadı');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchStats(); }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    const share = stats && stats.active_ios_users > 0
        ? Math.round((100 * stats.active_ios_with_token) / stats.active_ios_users) : 0;
    const maxVersion = Math.max(1, ...(stats?.by_version.map(v => v.users) || [1]));
    const maxSource = Math.max(1, ...(stats?.sources_24h.map(s => s.count) || [1]));

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Konum Push</h1>
                        <p className="text-sm text-gray-500 mt-1">Uygulama kapalıyken konum (Location Push, 2026.10.28+)</p>
                    </div>
                    <button onClick={fetchStats} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {error && <div className="bg-red-50 text-red-700 text-sm rounded-2xl p-4">{error}</div>}

                {stats && (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                                <p className="text-2xl font-bold text-purple-700">{fmt(stats.devices_with_token)}</p>
                                <p className="text-xs text-purple-600">Anahtarı olan cihaz</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                                <p className="text-2xl font-bold text-green-700">{fmt(stats.locationpush_last_hour)}</p>
                                <p className="text-xs text-green-600">Son 1 saat uzantı konumu</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                                <p className="text-2xl font-bold text-blue-700">{fmt(stats.locationpush_last_24h)}</p>
                                <p className="text-xs text-blue-600">Son 24 saat uzantı konumu</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-4">
                                <p className="text-2xl font-bold text-orange-700">%{share}</p>
                                <p className="text-xs text-orange-600">Aktiflerde anahtar ({stats.active_ios_with_token}/{stats.active_ios_users})</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Versions */}
                            <div className="bg-white rounded-2xl p-4">
                                <p className="font-medium text-gray-900 mb-3">Aktif iOS kullanıcıları — sürüm (7 gün)</p>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-gray-400 text-xs text-left">
                                            <th className="font-normal pb-1">Sürüm</th>
                                            <th className="font-normal pb-1">Kullanıcı</th>
                                            <th className="font-normal pb-1">Anahtarlı</th>
                                            <th className="w-1/3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.by_version.map(v => (
                                            <tr key={v.version}>
                                                <td className="py-1.5 text-gray-900">{v.version}</td>
                                                <td className="text-gray-700">{fmt(v.users)}</td>
                                                <td className="text-gray-700">{fmt(v.with_token)}</td>
                                                <td>
                                                    <div className={`h-2 rounded ${v.with_token > 0 ? 'bg-purple-500' : 'bg-gray-300'}`}
                                                        style={{ width: `${Math.max(2, (100 * v.users) / maxVersion)}%` }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Sources */}
                            <div className="bg-white rounded-2xl p-4">
                                <p className="font-medium text-gray-900 mb-3">Konum kaynakları — son 24 saat</p>
                                <table className="w-full text-sm">
                                    <tbody>
                                        {stats.sources_24h.map(s => (
                                            <tr key={s.source ?? 'none'}>
                                                <td className="py-1.5 text-gray-900">{s.label}</td>
                                                <td className="text-right text-gray-700">{fmt(s.count)}</td>
                                                <td className="w-1/3 pl-3">
                                                    <div className={`h-2 rounded ${SOURCE_COLORS[s.source ?? ''] || 'bg-gray-400'}`}
                                                        style={{ width: `${Math.max(2, (100 * s.count) / maxSource)}%` }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Recent devices */}
                        <div className="bg-white rounded-2xl p-4">
                            <p className="font-medium text-gray-900 mb-3">Anahtarı olan son cihazlar</p>
                            {stats.recent_devices.length === 0 ? (
                                <div className="text-center py-8">
                                    <RadioTower className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                    <p className="text-gray-500 text-sm">Henüz anahtarı olan cihaz yok</p>
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-gray-400 text-xs text-left">
                                            <th className="font-normal pb-1">Kullanıcı</th>
                                            <th className="font-normal pb-1">Sürüm</th>
                                            <th className="font-normal pb-1">Son uzantı konumu</th>
                                            <th className="font-normal pb-1">Son 24 saat</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.recent_devices.map((d, i) => (
                                            <tr key={`${d.username}-${i}`}>
                                                <td className="py-1.5 text-gray-900">{d.username}</td>
                                                <td className="text-gray-700">{d.app_version || '—'}</td>
                                                <td className="text-gray-700">{timeAgo(d.last_fix)}</td>
                                                <td className="text-gray-700">{fmt(d.fixes_24h)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LocationPushStatus;
